export const STREAM_FAILURE_MESSAGE = {
    licence_refused: "This title is not available on this television.",
    licence_expired: "This title stopped being available. Try starting it again.",
    manifest_unreachable: "This title could not be loaded. Check the connection.",
    codec_unsupported: "This television cannot play this title's video format.",
    decoder_exhausted: "The television ran out of video capacity. Try again.",
    network_stalled: "The connection stopped while playing.",
    unknown: "This title could not be played.",
};
const SHAKA_CODE = {
    1001: "manifest_unreachable",
    1002: "manifest_unreachable",
    1003: "manifest_unreachable",
    3016: "unknown",
    3017: "decoder_exhausted",
    4012: "codec_unsupported",
    4032: "codec_unsupported",
    6001: "codec_unsupported",
    6003: "unknown",
    6005: "licence_refused",
    6007: "licence_refused",
    6008: "licence_refused",
    6012: "licence_refused",
    6014: "licence_expired",
    7000: "network_stalled",
};
const REFUSAL_STATUS = new Set([401, 402, 403]);
export function classifyStreamError(err) {
    var _a;
    const e = err;
    const status = ((_a = e === null || e === void 0 ? void 0 : e.data) !== null && _a !== void 0 ? _a : []).find((d) => typeof d === "number" && d >= 100 && d < 600);
    if (status !== undefined && REFUSAL_STATUS.has(status)) {
        return "licence_refused";
    }
    if (typeof (e === null || e === void 0 ? void 0 : e.code) === "number" && SHAKA_CODE[e.code]) {
        return SHAKA_CODE[e.code];
    }
    return "unknown";
}
export async function drmSupported(keySystem = "com.widevine.alpha") {
    const nav = typeof navigator === "undefined"
        ? undefined
        : navigator;
    if (typeof (nav === null || nav === void 0 ? void 0 : nav.requestMediaKeySystemAccess) !== "function")
        return false;
    try {
        await nav.requestMediaKeySystemAccess(keySystem, [
            {
                initDataTypes: ["cenc"],
                videoCapabilities: [{ contentType: 'video/mp4; codecs="avc1.42E01E"' }],
            },
        ]);
        return true;
    }
    catch {
        return false;
    }
}
export function drmServers(licence) {
    const servers = {};
    if (licence.widevineUrl)
        servers["com.widevine.alpha"] = licence.widevineUrl;
    if (licence.playreadyUrl) {
        servers["com.microsoft.playready"] = licence.playreadyUrl;
    }
    return servers;
}
const HD_PIXELS = 1920 * 1088;
export const CONSERVATIVE_ABR_CAP = {
    maxBitrate: 6000000,
    maxPixels: HD_PIXELS,
    source: "conservative_default",
    reason: "no measurement for this device class - held at the known-good 1080p / 6 Mbps encode",
};
export function rungContentType(rung) {
    var _a;
    return `${(_a = rung.mimeType) !== null && _a !== void 0 ? _a : "video/mp4"}; codecs="${rung.codecs}"`;
}
export async function probeDecodeSupport(rungs) {
    var _a;
    const caps = typeof navigator === "undefined"
        ? undefined
        : navigator.mediaCapabilities;
    if (typeof (caps === null || caps === void 0 ? void 0 : caps.decodingInfo) !== "function")
        return [];
    const answers = [];
    for (const rung of rungs) {
        try {
            const info = await caps.decodingInfo({
                type: "media-source",
                video: {
                    contentType: rungContentType(rung),
                    width: rung.width,
                    height: rung.height,
                    bitrate: rung.bitrate,
                    framerate: (_a = rung.frameRate) !== null && _a !== void 0 ? _a : 25,
                },
            });
            answers.push({
                rung,
                supported: info.supported === true,
                smooth: info.smooth === true,
                powerEfficient: info.powerEfficient === true,
            });
        }
        catch {
        }
    }
    return answers;
}
export function chooseAbrCap(input = {}) {
    var _a;
    const verdict = input.verdict;
    if (verdict && verdict.maxPlayableBitrate !== null) {
        return {
            maxBitrate: verdict.maxPlayableBitrate,
            maxPixels: verdict.maxPlayablePixels || CONSERVATIVE_ABR_CAP.maxPixels,
            source: "recorded_verdict",
            reason: `measured on ${verdict.model} firmware ${verdict.firmware}`,
        };
    }
    const answers = (_a = input.probe) !== null && _a !== void 0 ? _a : [];
    if (answers.length === 0)
        return CONSERVATIVE_ABR_CAP;
    const smooth = answers.filter((a) => a.supported && a.smooth);
    const efficient = smooth.filter((a) => a.powerEfficient);
    let best;
    let why;
    if (efficient.length > 0) {
        best = highest(efficient);
        why = "highest rendition the device calls power-efficient";
    }
    else if (smooth.length > 0) {
        best = highest(smooth);
        why =
            "device reports nothing power-efficient; held to its highest smooth rendition";
    }
    else {
        best = lowest(answers);
        why = "device claims no rendition plays smoothly; held to the lowest rung";
    }
    const bitrate = Math.min(best.rung.bitrate, CONSERVATIVE_ABR_CAP.maxBitrate);
    const pixels = Math.min(best.rung.width * best.rung.height, CONSERVATIVE_ABR_CAP.maxPixels);
    if (bitrate >= CONSERVATIVE_ABR_CAP.maxBitrate &&
        pixels >= CONSERVATIVE_ABR_CAP.maxPixels) {
        return CONSERVATIVE_ABR_CAP;
    }
    return {
        maxBitrate: bitrate,
        maxPixels: pixels,
        source: "device_probe",
        reason: `${why} (${Math.round(bitrate / 1000)} kbps)`,
    };
}
function highest(answers) {
    return answers.reduce((a, b) => (b.rung.bitrate > a.rung.bitrate ? b : a));
}
function lowest(answers) {
    return answers.reduce((a, b) => (b.rung.bitrate < a.rung.bitrate ? b : a));
}
export function abrCapConfig(cap) {
    const restrictions = {
        maxBandwidth: cap.maxBitrate,
        maxPixels: cap.maxPixels,
    };
    return { abr: { restrictions: { ...restrictions } }, restrictions };
}
